/**
 * URL 处理工具函数
 */

import { EXTERNAL_LINKS } from './external-links';

/**
 * UTM 参数接口
 */
export interface UTMParams {
    source?: string;
    medium?: string;
    content?: string;
    campaign?: string;
    term?: string;
}

/** 默认 UTM 配置 */
export const UTM_DEFAULTS = {
    source: 'enter',
    medium: 'web',
} as const;

/** UTM content 标识（按使用场景） */
export const UTM_CONTENT = {
    HOME: 'home',
    WORKSPACE: 'workspace',
    FOOTER_PRODUCTS: 'footer_products',
} as const;

/**
 * 为 URL 添加 UTM 参数
 * @param baseUrl - 基础 URL
 * @param params - UTM 参数
 * @returns 带 UTM 参数的 URL
 */
export function withUTM(baseUrl: string, params: UTMParams): string {
    const url = new URL(baseUrl);

    if (params.source) url.searchParams.set('utm_source', params.source);
    if (params.medium) url.searchParams.set('utm_medium', params.medium);
    if (params.content) url.searchParams.set('utm_content', params.content);
    if (params.campaign) url.searchParams.set('utm_campaign', params.campaign);
    if (params.term) url.searchParams.set('utm_term', params.term);

    return url.toString();
}

/**
 * 快捷方法：使用默认 source/medium 创建带追踪参数的链接
 * @param baseUrl - 基础 URL
 * @param content - utm_content 值（标识使用场景）
 * @returns 带 UTM 参数的 URL
 */
export function createTrackingLink(baseUrl: string, content: string): string {
    return withUTM(baseUrl, {
        source: UTM_DEFAULTS.source,
        medium: UTM_DEFAULTS.medium,
        content,
    });
}

/**
 * Forum Feedback 参数接口
 */
export interface ForumFeedbackParams {
    /** 当前项目 URL */
    projectUrl: string;
    /** 当前对话轮数 */
    projectTurn: number | string;
    /** Legacy 错误详情（通常为错误标题）；v1 系统错误改用 errorId */
    errorDetails?: string;
    /** v1 系统错误关联 ID，不包含原始技术诊断 */
    errorId?: string;
}

/**
 * 构建 Forum Feedback URL
 * @param params - Feedback 参数
 * @returns 带查询参数的 Forum URL
 * @example
 * buildForumFeedbackUrl({
 *   projectUrl: 'https://enter.converge.ai/project/123',
 *   projectTurn: 5,
 *   errorDetails: 'Something went wrong'
 * })
 * // => 'https://enter.converge.ai/forum?composer=open&category_slug=bug-report&project_url=...'
 */
export function buildForumFeedbackUrl(params: ForumFeedbackParams): string {
    const url = new URL(EXTERNAL_LINKS.FORUM);
    url.searchParams.set('composer', 'open');
    url.searchParams.set('category_slug', 'bug-report');
    url.searchParams.set('project_url', params.projectUrl);
    url.searchParams.set('project_turn', String(params.projectTurn));
    if (params.errorId) {
        url.searchParams.set('error_id', params.errorId);
    } else if (params.errorDetails) {
        url.searchParams.set('error_details', params.errorDetails);
    }
    return url.toString();
}
